import { Box } from '@/type/layoutEditor/Box';

export function getCombinedBoundingBox(...boxes: Box[]): Box {
  const x = Math.min(...boxes.map((box) => box.x));
  const y = Math.min(...boxes.map((box) => box.y));

  const right = Math.max(...boxes.map((box) => box.x + box.width));
  const bottom = Math.max(...boxes.map((box) => box.y + box.height));
  return { x, y, width: right - x, height: bottom - y };
}

export function boxMapper<T extends Box>({ x, y, width, height }: T): Box {
  return { x, y, width, height };
}

export function floorBoxCoordinates(box: Box): Box {
  return {
    x: Math.floor(box.x),
    y: Math.floor(box.y),
    width: Math.floor(box.width),
    height: Math.floor(box.height),
  };
}

export function areBoxesEqual<T extends Box, K extends Box>(a: T, b: K): boolean {
  return a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height;
}

export function calculateBoxDelta<T extends Box, K extends Box>(oldBox: T, newBox: K) {
  return {
    x: newBox.x - oldBox.x,
    y: newBox.y - oldBox.y,
    width: newBox.width - oldBox.width,
    height: newBox.height - oldBox.height,
  };
}

export function transformBoxWithDelta(
  box: Box,
  {
    x: deltaX = 0,
    y: deltaY = 0,
    width: deltaWidth = 0,
    height: deltaHeight = 0,
  }: Partial<Box>,
): Box {
  return {
    x: box.x + deltaX,
    y: box.y + deltaY,
    width: box.width + deltaWidth,
    height: box.height + deltaHeight,
  };
}

export function calculateCenterBox<T extends Box>(
  box: Box,
  width: number,
  height: number,
): Box {
  return {
    x: box.x + (box.width - width) / 2,
    y: box.y + (box.height - height) / 2,
    width,
    height,
  };
}

export function isNullBox(box: Box): boolean {
  return areBoxesEqual(box, nullBox);
}

export function isBoxDimensionsNull(box: Box): boolean {
  return box.width === 0 && box.height === 0;
}

export function isBoxPositionNull(box: Box): boolean {
  return box.x === 0 && box.y === 0;
}

export const nullBox: Box = {
  x: 0,
  y: 0,
  width: 0,
  height: 0,
};

export function isBoxOverflows(innerBox: Box, outerBox: Box): boolean {
  return (
    innerBox.x < outerBox.x ||
    innerBox.y < outerBox.y ||
    innerBox.x + innerBox.width > outerBox.x + outerBox.width ||
    innerBox.y + innerBox.height > outerBox.y + outerBox.height
  );
}

export function getOuterBox(width: number, height: number): Box {
  return {
    x: 0,
    y: 0,
    width,
    height,
  };
}

export function fitDimensionsWithinBounds(
  width: number,
  height: number,
  maxWidth: number,
  maxHeight: number,
): {
  width: number;
  height: number;
} {
  if (width <= maxWidth && height <= maxHeight) return { width, height };

  const scale = Math.min(maxWidth / width, maxHeight / height);

  return {
    width: Math.floor(width * scale),
    height: Math.floor(height * scale),
  };
}

export function boxesIntersect(a: Box, b: Box): boolean {
  return !(
    a.x + a.width <= b.x ||
    b.x + b.width <= a.x ||
    a.y + a.height <= b.y ||
    b.y + b.height <= a.y
  );
}
